import { useEffect, useRef, useState } from 'react';

/**
 * `touch-action` прокручиваемой середины переносимого виджета.
 *
 * <p>Виджет тянется за любое место, поэтому на нём `touch-action: none`. Но прокручиваемый
 * элемент внутри сбрасывает этот запрет для своих потомков, и палец, положенный на середину,
 * браузер забирал себе под прокрутку – даже когда прокручивать было нечего: жест обрывался
 * `pointercancel`, и виджет не ехал. Решать надо заранее, в CSS: браузер выбирает между
 * прокруткой и жестом в момент касания, до любого обработчика. Поэтому середина отдаёт палец
 * прокрутке (`pan-y`) только когда ей правда есть куда прокручиваться, иначе – `none`, и тянет
 * виджет, как любое другое место (01.10.2026).
 *
 * @param enabled следить только пока виджет на экране
 */
export const useScrollTouchAction = <T extends HTMLElement>(enabled: boolean) => {
  const ref = useRef<T | null>(null);
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const scroller = ref.current;
    if (!enabled || !scroller || typeof ResizeObserver === 'undefined') {
      return;
    }

    const measure = () => setScrollable(scroller.scrollHeight > scroller.clientHeight);
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    for (const child of scroller.children) {
      observer.observe(child);
    }
    measure();

    return () => observer.disconnect();
  }, [enabled]);

  return { ref, touchAction: scrollable ? 'pan-y' : 'none' } as const;
};
